import { Component, HostBinding, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, signal } from '@angular/core'
import { Router } from '@angular/router'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ApplicationArea, UserReviewClient } from 'src/app/shared/services/ppv.api'
import { GlobalAdminService } from 'src/app/dvm-tool/globalAdmins.service'
import { Subject } from 'rxjs'
import { take, takeUntil, finalize } from 'rxjs/operators'

@Component({
   selector: 'dvm-tool-landing',
   templateUrl: './dvm-tool-landing.component.html',
   styleUrls: ['./dvm-tool-landing.component.scss'],
   standalone: false,
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DVMToolLandingComponent implements OnDestroy {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   public isDVM = false
   public isDVMToolAdmin = false
   public isHM = false
   public isRM = false
   public isCOS = false
   public isCOSA = false
   public isOpsVP = false
   public isOpsDirector = false

   shouldDisplayDVMTile = false
   shouldDisplayTeamTile = false
   shouldDisplayOpenTile = false
   openReviewType: string | null = null

   private readonly REVIEW_TYPES = ['DVM', 'HM', 'COS', 'COSA', 'RM', 'DO', 'CLINICAL', 'CROSSTRAINED', 'RECEPTION', 'OPEN']

   isLoading = true
   messageCount = 0
   showMessages = false
   hasMessages = false

   public userId: number = null
   encodeURIComponent = encodeURIComponent

   isGlobalAdmin = this.globalAdminService.isGlobalAdmin

   private destroy$ = new Subject<void>()

   constructor(
      private authMgr: AuthManager,
      private userReviewClient: UserReviewClient,
      private router: Router,
      private cdr: ChangeDetectorRef,
      public globalAdminService: GlobalAdminService
   ) {
      this.authMgr
         .getCurrentUser()
         .pipe(take(1), takeUntil(this.destroy$))
         .subscribe((user) => {
            this.userId = user.id

            this.isDVMToolAdmin = this.authMgr.canAccess(user, {
               application: ApplicationArea.DVMTool,
               roleName: ['DVMToolAdmin'],
            })

            const cDVM = this.authMgr.canAccess(user, {
               application: ApplicationArea.DVMTool,
               roleName: ['DVMTool'],
            })

            const cRM = this.authMgr.canAccess(user, {
               application: ApplicationArea.DVMTool,
               roleName: ['RM'],
            })

            const cHM = this.authMgr.canAccess(user, {
               application: ApplicationArea.DVMTool,
               roleName: ['HospitalManager'],
            })

            const cOpsVP = this.authMgr.canAccess(user, {
               application: ApplicationArea.DVMTool,
               roleName: ['OpsVP'],
            })

            const cOpsDirector = this.authMgr.canAccess(user, {
               application: ApplicationArea.DVMTool,
               roleName: ['OpsDirector'],
            })

            this.isRM = user.dayforceJobCode === 'OPS-RM'
            this.isHM = user.dayforceJobCode === 'HM' || user.dayforceJobCode === 'AREA_MANAGER'
            this.isCOSA = user.dayforceJobCode === 'COSA'
            this.isCOS = user.dayforceJobCode === 'VET-COS'
            this.isOpsVP = user.dayforceJobCode === 'OPS-SVP' || user.dayforceJobCode === 'OPS-VP' || cOpsVP
            this.isOpsDirector = user.dayforceJobCode === 'CORP-DIR' || cOpsDirector
            this.isDVM = user.dayforceJobCode === 'VET'

            this.cdr.markForCheck()

            this.loadMessageCount()
            this.loadHasMessages()

            this.userReviewClient
               .getTileVisibility()
               .pipe(takeUntil(this.destroy$))
               .subscribe({
                  next: (v) => {
                     this.shouldDisplayDVMTile = v.showDvmTile
                     this.shouldDisplayTeamTile = v.showTeamTile
                     this.shouldDisplayOpenTile = v.showOpenTile
                     this.openReviewType = v.openReviewType ?? null
                     this.cdr.markForCheck()
                     this.isLoading = false
                  },
                  error: (err) => {
                     console.error('Error checking tile visibility:', err)
                     this.shouldDisplayDVMTile = false
                     this.shouldDisplayTeamTile = false
                     this.shouldDisplayOpenTile = false
                     this.openReviewType = null
                     this.cdr.markForCheck()
                     this.isLoading = false
                  },
               })
         })
   }

   ngOnDestroy(): void {
      clearTimeout(this.searchTimer)
      this.destroy$.next()
      this.destroy$.complete()
   }

   navigateToAdminDashboard(): void {
      this.router.navigate(['/performance-hub/admin'])
   }

   searchTerm = ''
   searchResults: any[] = []
   searching = false
   expandedId: number | null = null

   private searchTimer: any = null
   private searchSeq = 0

   private readonly RT_BY_JOB_CODE: Record<string, string> = {
      VET: 'DVM',
      HM: 'HM',
      AREA_MANAGER: 'HM',
      'VET-COS': 'COS',
      'OPS-RM': 'RM',
      'CORP-DIR': 'DO',
   }

   private readonly NON_TEAM_CODES = ['VET', 'HM', 'AREA_MANAGER', 'VET-COS', 'OPS-RM', 'CORP-DIR', 'COSA', 'OPS-VP', 'OPS-SVP', 'CORP-VP']

   private readonly AVATAR_PALETTES = [
      { bg: '#e0eef6', fg: '#0374b6' },
      { bg: '#daf3f2', fg: '#26b5ae' },
      { bg: '#ffefcc', fg: '#976a12' },
      { bg: '#fdebe4', fg: '#f05d44' },
      { bg: '#e9dee9', fg: '#831183' },
      { bg: '#fae8e8', fg: '#d80c47' },
   ]

   onSearch(term: string): void {
      this.searchTerm = term ?? ''
      this.expandedId = null
      clearTimeout(this.searchTimer)

      const q = this.searchTerm.trim()
      if (q.length < 2) {
         this.searchResults = []
         this.searching = false
         this.searchSeq++ // cancel anything in flight
         return
      }

      this.searching = true
      this.searchTimer = setTimeout(() => this.runSearch(q), 250)
   }

   private runSearch(q: string): void {
      const seq = ++this.searchSeq

      this.userReviewClient
         .searchUsers(q)
         .pipe(take(1), takeUntil(this.destroy$))
         .subscribe({
            next: (rows: any) => {
               if (seq !== this.searchSeq) return
               this.searchResults = rows ?? []
               this.searchResults.forEach((r) => this.splitPastRole(r))
               this.searching = false
               this.cdr.markForCheck()
            },
            error: (err) => {
               if (seq !== this.searchSeq) return
               console.error('Quick search failed:', err)
               this.searchResults = []
               this.searching = false
               this.cdr.markForCheck()
            },
         })
   }

   toggleReviewees(u: any, ev: Event): void {
      ev.stopPropagation()

      if (this.expandedId === u.revieweeUserId) {
         this.expandedId = null
         return
      }
      this.expandedId = u.revieweeUserId
      if (u._reviewees) return

      u._loadingReviewees = true
      this.userReviewClient
         .getRevieweesFor(u.revieweeUserId)
         .pipe(
            take(1),
            takeUntil(this.destroy$),
            finalize(() => {
               u._loadingReviewees = false
               this.cdr.markForCheck()
            })
         )
         .subscribe({
            next: (rows: any) => {
               u._reviewees = rows ?? []
               u._loadingReviewees = false
               this.cdr.markForCheck()
            },
            error: (err) => {
               console.error('Could not load reviewees:', err)
               u._reviewees = []
               u._loadingReviewees = false
               this.cdr.markForCheck()
            },
         })
   }

   @HostListener('document:click')
   onDocumentClick(): void {
      if (!this.searchResults.length) return
      this.searchResults = []
      this.expandedId = null
      this.cdr.markForCheck()
   }

   cycleOf(u: any): string {
      if (['CLINICAL', 'CROSSTRAINED', 'RECEPTION'].includes(u.reviewType)) return 'TEAM'
      if (['Q1', 'Q2', 'Q3', 'Q4'].includes(u.cycle)) {
         return `aDVM ${u.cycle}`
      } else if (u.cycle) {
         return `${u.cycle}`
      }

      switch (u.jobTitleCode) {
         case 'VET-COS':
            return 'COS'
         case 'HM':
            return 'HM'
         case 'OPS-RM':
         case 'AREA_MANAGER':
            return 'RM'
         case 'CORP-DIR':
            return 'DO'
         default:
            return ''
      }
   }

   cycleClass(u: any): string {
      const c = this.cycleOf(u)
      return c.startsWith('aDVM') ? 'ph-cyc-advm' : c ? 'ph-cyc-' + c.toLowerCase() : ''
   }

   typeOf(u: any): string {
      return u.reviewType === 'CLINICAL'
         ? 'Clinical'
         : u.reviewType === 'CROSSTRAINED'
           ? 'Cross-Trained'
           : u.reviewType === 'RECEPTION'
             ? 'Reception'
             : ''
   }

   isUnset(u: any): boolean {
      return !u.reviewType && !this.NON_TEAM_CODES.includes(u.jobTitleCode)
   }

   initials(name: string): string {
      return (name || '?')
         .split(' ')
         .filter(Boolean)
         .slice(0, 2)
         .map((w) => w[0])
         .join('')
         .toUpperCase()
   }

   private paletteFor(name: string): { bg: string; fg: string } {
      let h = 0
      for (let i = 0; i < (name || '').length; i++) h = (h + name.charCodeAt(i)) % this.AVATAR_PALETTES.length
      return this.AVATAR_PALETTES[h]
   }

   avatarBg(name: string): string {
      return this.paletteFor(name).bg
   }

   avatarFg(name: string): string {
      return this.paletteFor(name).fg
   }

   goToUser(u: any, ev: Event): void {
      ev.stopPropagation()
      this.router.navigate(['/performance-hub/admin', 'user-management'], {
         state: { pendingUserSelect: { revieweeUserId: u.revieweeUserId, practiceName: u.practiceName, revieweeDisplayName: u.revieweeDisplayName } },
      })
   }

   goToPractice(u: any, ev: Event): void {
      ev.stopPropagation()
      this.router.navigate(['/performance-hub/admin', 'user-management'], {
         state: { pendingUserSelect: { practiceName: u.practiceName } },
      })
   }

   goToRoster(u: any, ev: Event): void {
      ev.stopPropagation()
      this.router.navigate(['/performance-hub/team-reviews'], {
         queryParams: { rt: 'TEAM', q: u.revieweeDisplayName },
      })
   }

   reviewRt(u: any): string {
      if (u.reviewType && this.REVIEW_TYPES.includes(u.reviewType)) return u.reviewType
      if (u.cycle === 'OPEN') return 'OPEN'
      return this.RT_BY_JOB_CODE[u.jobTitleCode] || ''
   }

   get openTileRt(): string {
      return this.openReviewType || 'OPEN'
   }

   reviewPath(u: any): string {
      return ['CLINICAL', 'CROSSTRAINED', 'RECEPTION'].includes(this.reviewRt(u)) ? 'team-reviews' : 'reviews'
   }

   private hasPastRole(u: any): boolean {
      const rt = u.reviewType
      if (!rt || !this.REVIEW_TYPES.includes(rt)) return false

      const expected = this.RT_BY_JOB_CODE[u.jobTitleCode]
      if (expected) return rt !== expected

      return !['CLINICAL', 'CROSSTRAINED', 'RECEPTION', 'OPEN'].includes(rt)
   }

   private splitPastRole(u: any): void {
      if (!this.hasPastRole(u)) return

      u._past = { ...u, jobTitleCode: null }
      u.reviewType = null

      if (u._past.reviewType === 'DVM') u.cycle = null
   }

   goToReview(u: any, ev: Event): void {
      console.log(u)
      /*ev.stopPropagation()
      const rt = this.reviewRt(u)
      if (!rt) return

      const path = ['CLINICAL', 'CROSSTRAINED', 'RECEPTION'].includes(rt) ? 'team-reviews' : 'reviews'
      this.router.navigate(['/performance-hub', path, u.revieweeUserId], { queryParams: { rt } })
   */
   }

   loadHasMessages(): void {
      this.userReviewClient.hasAdminMessages().subscribe({
         next: (hasMessages) => {
            this.hasMessages = hasMessages
            this.cdr.detectChanges()
         },
      })
   }

   loadMessageCount(): void {
      this.userReviewClient.getAdminMessageAttentionCount().subscribe({
         next: (count) => {
            this.messageCount = count ?? 0
            this.cdr.detectChanges()
         },
         error: () => {
            this.messageCount = 0
            this.cdr.detectChanges()
         },
      })
   }

   closeMessages(): void {
      this.showMessages = false
      this.cdr.detectChanges()

      this.loadMessageCount()
   }
}
